import { LinearGradient } from "expo-linear-gradient";
import { useState, type ReactNode } from "react";
import { ActivityIndicator, Pressable, StyleSheet, View, type ViewStyle } from "react-native";

import {
  webControlHoverFadeTransition,
  webControlHoverTransition
} from "@/design/interactionMotion";
import { Text } from "@/design/Text";
import { fonts, gradients, palette, radius, space } from "@/design/tokens";

type ButtonVariant = "primary" | "secondary" | "tinted" | "ghost";
type ButtonSize = "md" | "lg";

type ButtonProps = {
  label: string;
  onPress?: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  disabled?: boolean;
  loading?: boolean;
  style?: ViewStyle;
  /** Artwork-derived gradient stops (see lib/color artGradient); overrides
   * the brand gradient on primary buttons so actions take on the recital's
   * cover-art palette. */
  tint?: readonly string[] | null;
};

export function Button({
  label,
  onPress,
  variant = "primary",
  size = "md",
  icon,
  disabled,
  loading,
  style,
  tint
}: ButtonProps) {
  const [hovered, setHovered] = useState(false);
  const gradient =
    variant === "primary" ? (tint && tint.length >= 2 ? tint : gradients.brand) : null;
  const color =
    gradient != null
      ? palette.inkOnAccent
      : variant === "tinted"
        ? palette.accent
        : variant === "secondary"
          ? palette.text
          : palette.textSoft;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      disabled={disabled || loading}
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={({ pressed }) => [
        styles.base,
        size === "lg" ? styles.lg : styles.md,
        variant === "secondary" && styles.secondary,
        variant === "secondary" && hovered && styles.secondaryHovered,
        variant === "tinted" && styles.tinted,
        variant === "tinted" && hovered && styles.tintedHovered,
        variant === "ghost" && hovered && styles.ghostHovered,
        pressed && styles.pressed,
        (disabled || loading) && styles.disabled,
        style
      ]}
    >
      {gradient ? (
        <LinearGradient
          colors={gradient as unknown as [string, string, ...string[]]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
      ) : null}
      {gradient ? (
        <View style={[styles.hoverGlow, hovered && styles.hoverGlowVisible]} />
      ) : null}
      {/* The label stays mounted (invisible) while loading so the button
          keeps its label-derived width; the spinner centers over it. */}
      <View style={[styles.content, loading && styles.contentHidden]}>
        {icon}
        <Text
          variant={size === "lg" ? "bodyStrong" : "smallStrong"}
          color={color}
          style={gradient ? undefined : styles.outlineLabel}
        >
          {label}
        </Text>
      </View>
      {loading ? (
        <View style={styles.spinnerOverlay}>
          <ActivityIndicator size="small" color={color} />
        </View>
      ) : null}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  base: {
    alignItems: "center",
    borderRadius: radius.pill,
    justifyContent: "center",
    overflow: "hidden",
    ...webControlHoverTransition
  },
  md: {
    minHeight: 40,
    paddingHorizontal: space.lg,
    paddingVertical: space.sm
  },
  lg: {
    minHeight: 52,
    paddingHorizontal: space.xl,
    paddingVertical: space.md
  },
  secondary: {
    backgroundColor: palette.coolWash,
    borderColor: palette.hairlineStrong,
    borderWidth: 1
  },
  secondaryHovered: {
    backgroundColor: palette.coolWashHover
  },
  tinted: {
    backgroundColor: "rgba(143, 168, 250, 0.12)",
    borderColor: "rgba(143, 168, 250, 0.3)",
    borderWidth: 1
  },
  tintedHovered: {
    backgroundColor: "rgba(143, 168, 250, 0.2)",
    borderColor: "rgba(143, 168, 250, 0.5)"
  },
  ghostHovered: {
    backgroundColor: palette.coolWash
  },
  hoverGlow: {
    backgroundColor: "rgba(255, 255, 255, 0.16)",
    bottom: 0,
    left: 0,
    opacity: 0,
    pointerEvents: "none",
    position: "absolute",
    right: 0,
    top: 0,
    ...webControlHoverFadeTransition
  },
  hoverGlowVisible: {
    opacity: 1
  },
  content: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.sm,
    justifyContent: "center"
  },
  contentHidden: {
    opacity: 0
  },
  spinnerOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center"
  },
  outlineLabel: {
    fontFamily: fonts.bodyMedium
  },
  pressed: {
    opacity: 0.85
  },
  disabled: {
    opacity: 0.45
  }
});
